/**
 * Operator attention for stalled seven-tree ingest. Freshness comes from VPS
 * scheduler ticks and tree-ingest snapshots, not property lastSeenAt.
 */
import { escapeAttr, escapeHtml, safeDateTimeLabel } from "./dashboard-html.js";
import type { PropertiesConveyorHealth } from "./dashboard-command-center.js";

/** Daily skip-tree-ingest cron is 12:17 UTC. Miss one day plus buffer. */
export const INGEST_STALE_AFTER_MS = 36 * 60 * 60 * 1000;
/** Scheduler cadence is 2 minutes. Five missed ticks plus buffer. */
export const INGEST_SCHEDULER_STALE_AFTER_MS = 10 * 60 * 1000;

export const TREE_INGEST_ORDER = [
  "logs_upcoming",
  "logs_held",
  "brock",
  "hutchens",
  "ingle",
  "bell",
  "mtg",
  "catawba"
] as const;

export const TREE_INGEST_LABELS: Record<(typeof TREE_INGEST_ORDER)[number], string> = {
  logs_upcoming: "Logs upcoming",
  logs_held: "Logs held",
  brock: "Brock and Scott",
  hutchens: "Hutchens",
  ingle: "Ingle",
  bell: "Bell Carrington",
  mtg: "MTG",
  catawba: "Catawba"
};

export type IngestTreeStatus = {
  id: (typeof TREE_INGEST_ORDER)[number];
  label: string;
  properties: number | null;
  lastSeenAt: string | null;
  lastAttemptAt: string | null;
  error: string | null;
  remaining: number | null;
  stale: boolean;
  missing: boolean;
};

export type PipelineAttentionAlert = {
  id: "ingest_stalled";
  title: string;
  detail: string;
  latestScrapeAt: string | null;
  ageLabel: string;
  trees: IngestTreeStatus[];
};

export function pipelineAttentionAlerts(
  health: PropertiesConveyorHealth | null,
  now: Date = new Date()
): PipelineAttentionAlert[] {
  if (!health?.tree) return [];
  const latest = ingestFreshnessAt(health);
  const latestMs = latest ? Date.parse(latest) : NaN;
  const staleAfter = health.ingest?.lastTickAt || health.ingest?.lastAttemptAt
    ? INGEST_SCHEDULER_STALE_AFTER_MS
    : INGEST_STALE_AFTER_MS;
  const stale = !Number.isFinite(latestMs) || now.getTime() - latestMs > staleAfter;
  if (!stale) return [];
  const trees = ingestTreeStatuses(health, now);
  const ageLabel = Number.isFinite(latestMs) ? ageFrom(latestMs, now) : "never";
  const when = latest ? safeDateTimeLabel(latest) : "never";
  const missing = trees.filter((row) => row.missing).map((row) => row.label);
  const missingNote = missing.length
    ? ` Missing from D1: ${missing.join(", ")}.`
    : "";
  const tickNote = health.ingest?.lastTickAt
    ? ` Last scheduler tick: ${safeDateTimeLabel(health.ingest.lastTickAt)}.`
    : "";
  const failNote = health.ingest?.lastFailureClass
    ? ` Last typed failure: ${health.ingest.lastFailureClass}.`
    : "";
  return [{
    id: "ingest_stalled",
    title: "Daily tree ingest is not running",
    detail:
      `Last VPS ingest evidence: ${when} (${ageLabel}).${tickNote}${failNote} Ingest is the VPS scheduler, not GitHub Actions. `
      + "HTTP trees (Bell, Catawba, Ingle, Brock, Hutchens) scrape on the control-plane. "
      + "Logs and MTG need local Chromium. "
      + `If this stays stale, a tree fetch missed (timeout, schema_changed, or browser).${missingNote}`,
    latestScrapeAt: latest,
    ageLabel,
    trees
  }];
}

export function ingestFreshnessAt(health: PropertiesConveyorHealth | null): string | null {
  return health?.ingest?.lastTickAt
    ?? health?.ingest?.lastAttemptAt
    ?? health?.tree?.latestScrapeAt
    ?? null;
}

export function ingestTreeStatuses(
  health: PropertiesConveyorHealth | null,
  now: Date = new Date()
): IngestTreeStatus[] {
  const sources = health?.tree?.sources ?? [];
  const bySource = new Map(sources.map((row) => [row.source, row]));
  const attempts = new Map((health?.ingest?.sources ?? []).map((row) => [row.source, row]));
  return TREE_INGEST_ORDER.map((id) => {
    const row = bySource.get(id);
    const attempt = attempts.get(id);
    const lastSeenAt = row?.lastSeenAt ?? null;
    const lastAttemptAt = attempt?.lastAttemptAt ?? null;
    const freshnessAt = lastAttemptAt ?? lastSeenAt;
    const lastMs = freshnessAt ? Date.parse(freshnessAt) : NaN;
    const missing = !row && !attempt;
    const stale = missing || !Number.isFinite(lastMs) || now.getTime() - lastMs > INGEST_STALE_AFTER_MS;
    return {
      id,
      label: TREE_INGEST_LABELS[id],
      properties: row ? row.properties : null,
      lastSeenAt,
      lastAttemptAt,
      error: attempt?.error ?? null,
      remaining: attempt?.remaining ?? null,
      stale,
      missing
    };
  });
}

export function pipelineAttentionHtml(alerts: readonly PipelineAttentionAlert[]): string {
  if (alerts.length === 0) return "";
  return alerts.map((alert) => `
    <aside class="pipeline-attention" data-pipeline-attention="${escapeAttr(alert.id)}" role="alert">
      <p class="pipeline-attention-kicker">Needs you</p>
      <h2>${escapeHtml(alert.title)}</h2>
      <p>${escapeHtml(alert.detail)}</p>
      <p class="pipeline-attention-links">
        <a href="/ingest">Open Ingest</a>
      </p>
    </aside>
  `).join("");
}

function ageFrom(thenMs: number, now: Date): string {
  const hours = Math.max(0, Math.floor((now.getTime() - thenMs) / (60 * 60 * 1000)));
  if (hours < 48) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}
